import FetchUtils from '../thunks/fetch_utils';

import { getBackendUri } from '../utils/EnvConfigUtils';
import { UploadFile, UploadFileStatus } from 'antd/lib/upload/interface';
import { ROLE_TYPES } from './DataConstants';
import { retrieveCachedUserDetails } from '../thunks/session_thunks';
import { nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';

export const FILE_EXTENSION_TO_MIME_TYPE_MAP = {
	'3gp': 'video/3gpp',
	mp4: 'video/mp4', //.mp4, .m4a, .m4p, .m4b, .m4r
	m4a: 'video/mp4', //.mp4, .m4a, .m4p, .m4b, .m4r
	m4p: 'video/mp4', //.mp4, .m4a, .m4p, .m4b, .m4r
	m4b: 'video/mp4', //.mp4, .m4a, .m4p, .m4b, .m4r
	m4r: 'video/mp4', //.mp4, .m4a, .m4p, .m4b, .m4r
	m1v: 'video/mpeg', //.m1v
	ogg: 'video/ogg', //.ogg
	mov: 'video/quicktime', //.mov, .qt
	qt: 'video/quicktime', //.mov, .qt
	webm: 'video/webm', //.webm
	m4v: 'video/x-m4v', //.m4v
	asf: 'video/ms-asf', //.asf, .wma, .wmv
	wma: 'video/ms-asf', //.asf, .wma, .wmv
	wmv: 'video/x-ms-wmv', //.wmv
	avi: 'video/x-msvideo',
};

export const extractFileExtensionFromFilename = (filename) => {
	const matches = /\.[^.\\/:*?"<>|\r\n]+$/.exec(filename);
	return matches[0];
};

// FIXME: make this work for supplier
export const getFile = (fileId, fileName, roleType = 'buyer') => {
	const BACKEND_URI = getBackendUri();
	const fetchUtils = new FetchUtils();
	return fetchUtils.get(
		`${BACKEND_URI}/api/v1/${roleType}/file/download/${encodeURIComponent(
			fileId
		)}/${encodeURIComponent(fileName)}/${encodeURIComponent(
			retrieveCachedUserDetails(['token']).token
		)}`,
		{}
	);
};

export const getFileMimeTypeFromString = (fileString) => {
	if (!fileString || fileString.length === 0) {
		return '';
	}
	const fileExtension = extractFileExtensionFromFilename(fileString);
	const mimeType = FILE_EXTENSION_TO_MIME_TYPE_MAP[fileExtension];
	return mimeType;
};

export const getFileLinkFromString = (userType) => (fileString) => {
	const [fileId, fileName] = fileString.split('/');
	const BACKEND_URI = getBackendUri();
	return `${BACKEND_URI}/api/v1/${userType}/file/download/${encodeURIComponent(
		fileId
	)}/${encodeURIComponent(fileName)}/${encodeURIComponent(
		retrieveCachedUserDetails(['token']).token
	)}`;
};

export const getProtectedImageUriGeneric = (fileId, fileName) =>
	`https://images.useopenwrench.com/${encodeURIComponent(fileId)}/${encodeURIComponent(fileName)}`;

export const getProtectedImageUri = (userType) => (fileId, fileName, width, height) =>
	getProtectedImageUriGeneric(fileId, fileName);

export const getProtectedImageUriFromString = (userType) => (photoString) => {
	const [fileId, fileName] = photoString.split('/');
	return `https://images.useopenwrench.com/${encodeURIComponent(fileId)}/${encodeURIComponent(
		fileName
	)}`;
};

export const getProtectedImageUriFromStringResized = (userType) => (photoString, width, height) => {
	const [fileId, fileName] = photoString.split('/');
	const BACKEND_URI = getBackendUri();
	return `${BACKEND_URI}/api/v1/${userType}/file/download_resize/${encodeURIComponent(
		fileId
	)}/${encodeURIComponent(fileName)}/${width}x${height}/${encodeURIComponent(
		retrieveCachedUserDetails(['token']).token
	)}`;
};

export const getVideoUriFromString = (fileIdWithName) => {
	const [fileId, fileName] = fileIdWithName.split('/');
	return `https://images.useopenwrench.com/${encodeURIComponent(fileId)}/${encodeURIComponent(
		fileName
	)}`;
};

export const getProtectedImageUriForBuyer = getProtectedImageUri(ROLE_TYPES.BUYER);
export const getProtectedImageUriForSupplier = getProtectedImageUri(ROLE_TYPES.SUPPLIER);

export const sanitizeFileName = (fileName: string): string => {
	const sanitized = fileName.replace(/[#?%]/g, '');
	const extensionIndex = sanitized.lastIndexOf('.');
	const baseName = extensionIndex > 0 ? sanitized.substring(0, extensionIndex) : sanitized;
	return baseName
		? sanitized
		: 'file' + (extensionIndex >= 0 ? sanitized.substring(extensionIndex) : '');
};

export const getFileLink = (fileId, fileName, roleType = 'buyer'): string => {
	return `https://images.useopenwrench.com/${fileId}/${encodeURIComponent(fileName)}`;
};

// Resolves the durable URL of a file that OWUpload just finished uploading.
// The presigned-upload response shape varies by backend: the real backend
// returns fileId/fileName (from which getFileLink builds the CDN URL), while
// some responses/mocks return fileUrl or url directly. Falling back to the
// presigned PUT URL with its query string stripped covers the rest. Returns
// null when the file has no usable URL (e.g. still uploading or errored).
export const getUploadedFileUrl = (file): string | null => {
	if (!file) {
		return null;
	}
	const directUrl =
		nullSafeGet('response.data.fileUrl', file) || nullSafeGet('response.data.url', file);
	if (directUrl) {
		return directUrl;
	}
	const fileId = nullSafeGet('response.data.fileId', file);
	if (fileId) {
		const fileName = nullSafeGetOrElse('response.data.fileName', file, file.name);
		return getFileLink(fileId, fileName);
	}
	const presignedUrl = nullSafeGet('response.data.presignedUrl', file);
	return presignedUrl ? presignedUrl.split('?')[0] : null;
};

export function getBase64(img, callback) {
	const reader = new FileReader();
	reader.addEventListener('load', () => callback(reader.result));
	reader.readAsDataURL(img);
}

interface FileAttachment {
	fileId: string;
	fileName: string;
}

export function convertArrayOfObjectsToCSV(data, columnDelimiter = ',', lineDelimiter = '\n') {
	let result, ctr, keys;

	if (data == null || !data.length) {
		return null;
	}

	keys = Object.keys(data[0]);

	result = '';
	result += keys.join(columnDelimiter);
	result += lineDelimiter;

	data.forEach(function (item) {
		ctr = 0;
		keys.forEach(function (key) {
			if (ctr > 0) result += columnDelimiter;

			result += item[key];
			ctr++;
		});
		result += lineDelimiter;
	});

	return result;
}

export const getDefaultFileListFromString = (fileAttachments, userType = 'buyer'): UploadFile[] =>
	fileAttachments.map((fa, idx) => {
		const [fileId, fileName] = fa.split('/');
		const fileStatus: UploadFileStatus = 'done';
		const fileUrl = getFileLink(fileId, fileName, userType);
		return {
			uid: idx * -1,
			size: 42,
			name: fileName,
			status: fileStatus,
			response: {
				data: {
					fileId: fileId,
					fileName: fileName,
				},
			},
			url: fileUrl,
			thumbUrl: fileUrl,
			type: 'application/pdf',
		};
	});

export const getDefaultFileList = (fileAttachments, userType = 'buyer'): UploadFile[] =>
	fileAttachments.map((fa, idx) => {
		const fileStatus: UploadFileStatus = 'done';
		const fileUrl = getFileLink(fa.fileId, fa.fileName, userType);
		return {
			uid: idx * -1,
			size: 42,
			name: fa.fileName,
			status: fileStatus,
			response: {
				data: {
					fileId: fa.fileId,
					fileName: fa.fileName,
				},
			},
			url: fileUrl,
			type: 'application/pdf',
		};
	});

export const getDefaultFileListForImages = (fileDetails) =>
	fileDetails.map((file, idx) => {
		const { fileName, fileId } = file;
		const uri = getProtectedImageUriGeneric(fileId, fileName);
		return {
			uid: idx,
			size: 1,
			name: fileName,
			filename: fileName,
			url: uri,
			status: 'done',
			thumbUrl: uri,
			response: { data: { fileName, fileId } },
			type: 'image/jpeg',
		};
	});

// --- `images` search param: handing prefilled photos into a create-work-order wizard -------------
//
// The walkthrough/asset/linked-WO surfaces prefill the wizard's photo step by passing the source
// entity's attachments through the URL as `images`. The wire format is one comma-separated list of
// percent-encoded `fileId/fileName` pairs, and BOTH sides must agree on that order — reading it back
// as `fileName/fileId` persists an unreachable CDN path on the created work order, so the photo shows
// on the source record but not on the work order. Build the value with
// `buildImagesSearchParamValue` and parse it with `getPrefilledPhotosFromImagesSearchParam`; don't
// hand-roll either side.

export const buildImagesSearchParamValue = (attachments): string =>
	(attachments || [])
		.map((attachment) => {
			const fileId = nullSafeGetOrElse('fileId', attachment, '');
			const fileName = nullSafeGetOrElse('fileName', attachment, '');
			// Each pair is encoded on its own so a comma inside a filename — legal, and explicitly
			// tolerated by the backend's FileDetails parser — can't split one photo into two broken
			// references when the list is split back apart.
			return encodeURIComponent(`${fileId}/${fileName}`);
		})
		.join(',');

const decodeImagesSearchParamEntry = (entry: string): string => {
	try {
		return decodeURIComponent(entry);
	} catch (_) {
		// A link built before the entries were encoded may hold a bare `%` from the filename, which
		// decodeURIComponent rejects — such a value is already in its decoded form.
		return entry;
	}
};

export const parseImagesSearchParamValue = (imagesParamValue: string): string[] =>
	(imagesParamValue || '')
		.split(',')
		.filter((entry) => entry.length > 0)
		.map(decodeImagesSearchParamEntry);

// Rebuilds the antd Upload `fileList` entries for photos prefilled through the `images` param, in the
// shape the wizards' `images` form field and their `picturesInfo` submit mapping both expect.
export const getPrefilledPhotosFromImagesSearchParam = (imagesParamValue: string, roleType) =>
	parseImagesSearchParamValue(imagesParamValue).map((photoString) => {
		const separatorIndex = photoString.indexOf('/');
		const fileId = separatorIndex === -1 ? photoString : photoString.substring(0, separatorIndex);
		const fileName = separatorIndex === -1 ? '' : photoString.substring(separatorIndex + 1);
		const getUri = getProtectedImageUri(roleType);
		return {
			uid: fileId,
			lastModified: Date.now(),
			lastModifiedDate: new Date().toISOString(),
			name: fileName,
			type: 'image',
			percent: 100,
			originFileObj: {
				uid: fileId,
			},
			status: 'done',
			url: getUri(fileId, fileName, 800, 800),
			thumbUrl: getUri(fileId, fileName, 100, 100),
			response: {
				data: {
					fileName: fileName,
					fileId: fileId,
				},
			},
		};
	});
